import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  styled,
  Grid,
  Tooltip,
  Modal,
  IconButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  parsePdf,
  ResumeData,
  ExperienceModel,
  ProjectModel,
  ContestModel,
  MiscInfoModel,
  PositionEnum,
  ScoreModel,
} from "../services/parse";
import { updateProfile } from "../services/profile";
import { isApiError } from "../services/api";
import StudentCard from "./StudentCard";
import { VoteCandidate } from "../types";
import CompanyAutocomplete, { Company } from "./ui/CompanyAutocomplete";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const globalStylesToSx = (classNames: string) => {
  const sx: any = {};
  if (classNames.includes("form-paper")) {
    sx.border = "1px solid rgba(51, 51, 51, 0.2)";
    sx.borderRadius = "0.375rem";
    sx.backgroundColor = "transparent";
    sx.marginBottom = "1rem";
    sx.padding = "1rem";
    sx["&:hover"] = {
      borderColor: "rgba(51, 51, 51, 0.4)",
    };
  }
  if (classNames.includes("form-section-title")) {
    sx.fontWeight = 300;
    sx.letterSpacing = "0.025em";
    sx.marginBottom = "0.75rem";
  }
  if (classNames.includes("form-button")) {
    sx.border = "1px solid rgba(51, 51, 51, 0.2)";
    sx.borderRadius = "0.375rem";
    sx.padding = "0.5rem 1rem";
    sx.fontWeight = 300;
    sx.fontSize = "0.875rem";
    sx.backgroundColor = "transparent";
    sx.color = "#333333";
    sx.textTransform = "none";
    sx["&:hover:not(:disabled)"] = {
      borderColor: "rgba(51, 51, 51, 0.6)",
      backgroundColor: "rgba(51, 51, 51, 0.05)",
    };
  }
  if (classNames.includes("form-button-primary")) {
    sx.backgroundColor = "#333333";
    sx.color = "#f8f8f8";
    sx["&:hover:not(:disabled)"] = {
      backgroundColor: "#444444",
      borderColor: "#444444",
    };
  }
  return sx;
};

const positionDisplayMap: Record<PositionEnum, string> = {
  [PositionEnum.ACADEMIA]: "academic",
  [PositionEnum.DATA_SCIENCE]: "quant",
  [PositionEnum.ENGINEER]: "engineer",
};
// TODO: consolidate ResumeData and ProfileData; into an interface.
const initialFormData: ResumeData = {
  name: "",
  email: "",
  class_year: undefined,
  position: undefined,
  experience: [],
  projects: [],
  contests: [],
  misc: [],
  scores: [],
};

interface EditProfileFormProps {
  userProfile: {
    id: string;
    email?: string;
    name?: string;
  };
  initialProfileData?: ResumeData;
  onSaveSuccess: () => void | Promise<void>;
}

const EditProfileForm: React.FC<EditProfileFormProps> = ({
  userProfile,
  initialProfileData,
  onSaveSuccess,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<ResumeData>(() => {
    if (initialProfileData) {
      return {
        ...initialProfileData,
        email: userProfile?.email || initialProfileData.email || "",
        name: userProfile?.name || initialProfileData.name || "",
      };
    }
    return {
      ...initialFormData,
      name: userProfile?.name || "",
      email: userProfile?.email || "",
    };
  });
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "success" | "error" | "saving"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sectionLimitExceeded, setSectionLimitExceeded] =
    useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scoresRef = useRef<HTMLDivElement | null>(null);
  const [previewData, setPreviewData] = useState<VoteCandidate | null>(null);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [validationTooltip, setValidationTooltip] = useState<string>("");
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(false);

  // Check if user has .edu email
  useEffect(() => {
    const email = userProfile?.email;
    if (email && !email.endsWith('.edu')) {
      setIsFormDisabled(true);
      setErrorMessage('Only .edu email addresses are allowed');
    }
  }, [userProfile?.email]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === "application/pdf") {
        setSelectedFile(file);
        setErrorMessage("");
        setUploadStatus("idle");
      } else {
        setSelectedFile(null);
        setErrorMessage("Please upload a PDF file");
      }
    }
  };

  // Function to check for exceeded sections and generate message
  const checkForExceededSections = (data: ResumeData) => {
    const MAX_ITEMS = 3;
    const sectionsExceeded = [];

    if (data.experience.length > MAX_ITEMS)
      sectionsExceeded.push(
        `experiences (${data.experience.length}/${MAX_ITEMS})`
      );
    if (data.projects.length > MAX_ITEMS)
      sectionsExceeded.push(`works (${data.projects.length}/${MAX_ITEMS})`);
    if (data.contests.length > MAX_ITEMS)
      sectionsExceeded.push(`contests (${data.contests.length}/${MAX_ITEMS})`);
    if (data.scores.length > MAX_ITEMS)
      sectionsExceeded.push(`scores (${data.scores.length}/${MAX_ITEMS})`);
    if (data.misc.length > MAX_ITEMS)
      sectionsExceeded.push(`misc items (${data.misc.length}/${MAX_ITEMS})`);

    const hasExceededSections = sectionsExceeded.length > 0;
    setSectionLimitExceeded(hasExceededSections);

    if (hasExceededSections) {
      return `Your resume has too many entries: ${sectionsExceeded.join(
        ", "
      )}. Maximum is ${MAX_ITEMS} per section. Please remove some entries to save.`;
    }
    return "";
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setErrorMessage("Please select a PDF file to upload");
      return;
    }
    try {
      setUploadStatus("uploading");
      setErrorMessage("");
      const responseData = await parsePdf(selectedFile);

      const parsedData = {
        name: responseData.name ?? userProfile?.name ?? "",
        email: responseData.email ?? userProfile?.email ?? "",
        class_year: responseData.class_year ?? undefined,
        position: responseData.position ?? undefined,
        experience: responseData.experience ?? [],
        projects: responseData.projects ?? [],
        contests: responseData.contests ?? [],
        misc: responseData.misc ?? [],
        scores: responseData.scores ?? [],
      };

      // Check if any sections exceed limits
      const exceededSectionsMessage = checkForExceededSections(parsedData);
      if (exceededSectionsMessage) {
        setErrorMessage(exceededSectionsMessage);
      }

      setFormData(parsedData);
      setUploadStatus("idle");
      setSelectedFile(null);
    } catch (error) {
      console.error("Error parsing PDF:", error);
      setUploadStatus("error");
      if (isApiError(error)) {
        setErrorMessage(error.message || "Failed to parse resume.");
      } else if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage("An unexpected error occurred during parsing.");
      }
    }
  };

  const handleFormDataChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === "class_year"
          ? value
            ? parseInt(value, 10)
            : undefined
          : name === "position"
            ? value || undefined
            : type === "number"
              ? value
                ? parseInt(value, 10)
                : undefined
              : value,
    }));
  };

  const handleListItemChange = (
    listName: keyof ResumeData,
    index: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = event.target;
    const isCheckbox = type === "checkbox";

    setFormData((prev) => {
      const list = prev[listName] as any[];
      if (!list || index < 0 || index >= list.length) return prev;
      const updatedList = [...list];
      updatedList[index] = {
        ...updatedList[index],
        [name]: isCheckbox ? (event.target as HTMLInputElement).checked : value,
      };
      return {
        ...prev,
        [listName]: updatedList,
      };
    });
  };

  const handleAddListItem = (
    listName: keyof ResumeData,
    newItemTemplate: any
  ) => {
    setFormData((prev) => {
      const list = prev[listName] as any[];
      if (listName === "misc") {
        newItemTemplate.key = `misc_item_${list.length + 1}`;
      }
      return {
        ...prev,
        [listName]: [...list, newItemTemplate],
      };
    });
  };

  const handleRemoveListItem = (listName: keyof ResumeData, index: number) => {
    setFormData((prev) => {
      const list = prev[listName] as any[];
      if (!list || index < 0 || index >= list.length) return prev;
      const updatedList = list.filter((_, i) => i !== index);
      return {
        ...prev,
        [listName]: updatedList,
      };
    });
  };

  const processDataBeforeSave = (data: any): ResumeData => {
    if (data.experience && Array.isArray(data.experience)) {
      data.experience = data.experience.map((exp: any) => {
        const cleanExp = { ...exp };
        delete cleanExp.start_date;
        delete cleanExp.end_date;
        return cleanExp;
      });
    }
    if (data.scores && Array.isArray(data.scores)) {
      data.scores = data.scores.map((score: any, index: number) => ({
        ...score,
        verified: false,
        id: score.id ? Number(score.id) : -1 - index,
        created_at: score.created_at || new Date().toISOString(),
      }));
    }
    delete data.email;
    return data;
  };

  const getValidationIssues = (data: ResumeData): string[] => {
    const issues: string[] = [];

    if (!data.name?.trim()) {
      issues.push("Name is required in Basic Information.");
    }
    if (!data.email?.trim()) {
      issues.push("Email is required in Basic Information.");
    }
    if (!data.class_year) {
      issues.push("Class Year is required in Basic Information.");
    }

    // Check if at least one section has an item
    const hasAnyItems =
      data.experience.length > 0 ||
      data.projects.length > 0 ||
      data.contests.length > 0 ||
      data.misc.length > 0 ||
      data.scores.length > 0;

    if (!hasAnyItems) {
      issues.push(
        "Please add at least one item in any section (experience, works, contests, scores, or miscellaneous)."
      );
    }

    // Section limits are now handled by the checkForExceededSections function

    data.experience.forEach((exp, index) => {
      if (!exp.company?.trim()) {
        issues.push(`Experience ${index + 1}: Company is required.`);
      }
      if (!exp.title?.trim()) {
        issues.push(`Experience ${index + 1}: Title is required.`);
      }
    });

    data.contests.forEach((contest, index) => {
      if (!contest.name?.trim()) {
        issues.push(`Contest ${index + 1}: Name is required.`);
      }
    });

    data.projects.forEach((project, index) => {
      if (!project.name?.trim()) {
        issues.push(`Project ${index + 1}: Name is required.`);
      }
      if (!project.description?.trim()) {
        issues.push(`Project ${index + 1}: Impact/description is required.`);
      }
    });

    data.scores.forEach((score, index) => {
      if (!score.name?.trim()) {
        issues.push(`Score ${index + 1}: Name is required.`);
      }
      if (
        score.value === undefined ||
        score.value === null ||
        String(score.value).trim() === ""
      ) {
        issues.push(`Score ${index + 1}: Value is required.`);
      }
    });

    data.misc.forEach((item, index) => {
      if (!item.value?.trim()) {
        issues.push(`Misc Item ${index + 1}: Value is required.`);
      }
    });
    return issues;
  };

  useEffect(() => {
    const issues = getValidationIssues(formData);

    // Check for section limits dynamically
    const exceededSectionsMessage = checkForExceededSections(formData);
    if (exceededSectionsMessage && errorMessage === "") {
      setErrorMessage(exceededSectionsMessage);
    } else if (
      !exceededSectionsMessage &&
      errorMessage.includes("Your resume has too many entries")
    ) {
      setErrorMessage("");
    }

    if (issues.length === 0 && !sectionLimitExceeded) {
      setIsFormValid(true);
      setValidationTooltip("");
    } else {
      setIsFormValid(false);
      setValidationTooltip(issues[0] || "");
    }
  }, [formData, errorMessage, sectionLimitExceeded]);

  const handleSaveProfile = async () => {
    setErrorMessage("");
    const validationIssues = getValidationIssues(formData);
    if (validationIssues.length > 0) {
      setErrorMessage(validationIssues[0]);
      return;
    }

    setUploadStatus("saving");
    try {
      const dataCopy = JSON.parse(JSON.stringify(formData));
      const dataToSave = processDataBeforeSave(dataCopy);
      if (!userProfile.email) {
        console.error(
          "User email not found in userProfile for updateProfile call"
        );
        setErrorMessage(
          "Your email is missing. Cannot save profile. Please try logging out and in again."
        );
        setUploadStatus("error");
        return;
      }

      await updateProfile(dataToSave, userProfile.email);
      setUploadStatus("success");
      if (typeof onSaveSuccess === "function") {
        onSaveSuccess();
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      setUploadStatus("error");
      if (isApiError(error)) {
        if (error.message?.includes("row-level security policy")) {
          setErrorMessage(
            "database permission error - please contact an administrator. (rls policy violation)"
          );
        } else {
          setErrorMessage(error.message || "Failed to save profile.");
        }
      } else if (error instanceof Error) {
        if (error.message?.includes("row-level security policy")) {
          setErrorMessage(
            "database permission error - please contact an administrator. (rls policy violation)"
          );
        } else {
          setErrorMessage(error.message);
        }
      } else {
        setErrorMessage("An unexpected error occurred while saving.");
      }
    }
  };

  useEffect(() => {
    const transformedPreviewData: VoteCandidate = {
      id: parseInt(userProfile.id, 10) || 0,
      class_year: formData.class_year,
      position: formData.position,
      experiences: formData.experience.map((exp) => ({
        id: 0,
        user_id: 0,
        company: exp.company,
        title: exp.title,
        description: exp.description,
        company_url: exp.company_url,
        created_at: "",
        updated_at: "",
      })),
      projects: formData.projects.map((proj) => ({
        id: 0,
        user_id: 0,
        name: proj.name,
        description: proj.description,
        created_at: "",
        updated_at: "",
      })),
      contests: formData.contests.map((con) => ({
        id: 0,
        user_id: 0,
        name: con.name,
        description: con.description,
        created_at: "",
        updated_at: "",
      })),
      misc: formData.misc.map((m) => ({
        id: 0,
        user_id: 0,
        key: m.key || "",
        value: m.value,
        created_at: "",
        updated_at: "",
      })),
      scores: formData.scores.map((score) => ({
        id: 0,
        user_id: 0,
        name: score.name || null,
        value: score.value !== undefined ? score.value : null,
        verified: false,
        created_at: "",
      })),
    };
    setPreviewData(transformedPreviewData);
  }, [formData, userProfile.id]);

  const handleOpenPreviewModal = () => setPreviewModalOpen(true);
  const handleClosePreviewModal = () => setPreviewModalOpen(false);

  return (
    <Grid
      container
      spacing={0}
      sx={{ flexGrow: 1, minHeight: 0, height: "100%" }}
    >
      {/* Left Column: Form Content */}
      <Grid
        size={{ xs: 12, md: 7 }}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRight: { md: 1 },
          borderColor: { md: "rgba(51, 51, 51, 0.2)" },
          position: "relative",
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            p: { xs: 2, sm: 2.5, md: 3 },
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "#f8f8f8",
          }}
        >
          {/* Error message */}
          {errorMessage && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity="error"
                onClose={() => setErrorMessage("")}
                sx={{ fontWeight: 300 }}
              >
                {errorMessage.toLowerCase()}
              </Alert>
            </Box>
          )}

          {/* Success message */}
          {uploadStatus === "success" && !errorMessage && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="success" sx={{ fontWeight: 300 }}>
                Profile saved successfully! Redirecting to voting...
              </Alert>
            </Box>
          )}

          {/* Mobile validation message */}
          <Box
            sx={{
              display: { xs: "block", md: "none" },
              mb: 2,
              position: "sticky",
              top: 0,
              zIndex: 3,
              bgcolor: "#f8f8f8",
              pt: 1,
            }}
          >
            {!isFormValid && validationTooltip && (
              <Alert
                severity="warning"
                sx={{
                  fontWeight: 300,
                  fontSize: "0.875rem",
                  py: 0.5,
                }}
              >
                {validationTooltip.toLowerCase()}
              </Alert>
            )}
          </Box>

          {/* Resume Upload Section */}
          <Paper
            elevation={0}
            sx={{ ...globalStylesToSx("form-paper"), p: 2, mb: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="div"
                sx={{
                  ...globalStylesToSx("form-section-title"),
                  mr: 0.5,
                  mb: 0,
                  fontSize: "1rem",
                  textTransform: "lowercase",
                }}
              >
                autofill with resume (optional)
              </Typography>
              <Tooltip title="quickly populate fields by uploading your pdf resume.">
                <HelpOutlineIcon
                  sx={{ fontSize: "1rem", color: "text.secondary" }}
                />
              </Tooltip>
            </Box>
            <Grid container spacing={1} alignItems="center">
              <Grid size={{ xs: 12, sm: selectedFile ? 6 : 12 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ ...globalStylesToSx("form-button"), width: "100%" }}
                  disabled={uploadStatus === "uploading" || isFormDisabled}
                >
                  {selectedFile
                    ? selectedFile.name.toLowerCase()
                    : "select pdf resume"}
                  <VisuallyHiddenInput
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    disabled={uploadStatus === "uploading" || isFormDisabled}
                  />
                </Button>
              </Grid>
              {selectedFile && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Button
                    variant="text"
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    disabled={uploadStatus === "uploading" || isFormDisabled}
                    sx={{
                      ...globalStylesToSx("form-button"),
                      width: "100%",
                      borderColor: "transparent",
                      "&:hover:not(:disabled)": {
                        borderColor: "rgba(51, 51, 51, 0.2)",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    clear
                  </Button>
                </Grid>
              )}
              {selectedFile && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploadStatus === "uploading" || !selectedFile || isFormDisabled}
                    sx={{
                      ...globalStylesToSx("form-button"),
                      ...globalStylesToSx("form-button-primary"),
                      width: "100%",
                    }}
                  >
                    {uploadStatus === "uploading" ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CircularProgress
                          size={16}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        parsing...
                      </Box>
                    ) : (
                      "parse"
                    )}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Basic Information Section */}
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                ...globalStylesToSx("form-section-title"),
                mb: 1.5,
                fontSize: "1rem",
                textTransform: "lowercase",
              }}
            >
              basic information
            </Typography>
            <TextField
              InputLabelProps={{ sx: { fontWeight: 300, color: "#333333" } }}
              inputProps={{
                sx: { fontWeight: 300, color: "#333333" },
                maxLength: 50
              }}
              label="email"
              name="email"
              type="email"
              value={formData.email || ""}
              onChange={handleFormDataChange}
              fullWidth
              variant="outlined"
              size="small"
              required
              disabled={true}
              sx={{ mb: 1.5 }}
            />
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  InputLabelProps={{
                    sx: { fontWeight: 300, color: "#333333" },
                  }}
                  inputProps={{
                    sx: { fontWeight: 300, color: "#333333" },
                    maxLength: 50
                  }}
                  label="name"
                  name="name"
                  value={formData.name || ""}
                  onChange={handleFormDataChange}
                  fullWidth
                  variant="outlined"
                  size="small"
                  required
                  disabled={isFormDisabled}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  InputLabelProps={{
                    sx: { fontWeight: 300, color: "#333333" },
                  }}
                  select
                  label="class year"
                  name="class_year"
                  value={formData.class_year || ""}
                  onChange={handleFormDataChange}
                  fullWidth
                  variant="outlined"
                  required
                  SelectProps={{
                    native: true,
                    sx: { fontWeight: 300, color: "#333333" },
                  }}
                  size="small"
                  disabled={isFormDisabled}
                >
                  <option value=""></option>
                  {[2025, 2026, 2027, 2028].map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </TextField>
              </Grid>
            </Grid>
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <TextField
                InputLabelProps={{ sx: { fontWeight: 300, color: "#333333" } }}
                select
                label="primary position"
                name="position"
                value={formData.position || ""}
                onChange={handleFormDataChange}
                fullWidth
                variant="outlined"
                size="small"
                SelectProps={{
                  native: true,
                  sx: { fontWeight: 300, color: "#333333" },
                }}
                sx={{ flexGrow: 1 }}
                disabled={isFormDisabled}
              >
                <option value=""></option>
                {Object.values(PositionEnum).map((pos) => (
                  <option key={pos} value={pos}>
                    {positionDisplayMap[pos] || pos}
                  </option>
                ))}
              </TextField>
              <Tooltip title="this is for your aspiring career path, currently not used in head-to-head.">
                <HelpOutlineIcon
                  sx={{
                    fontSize: "1rem",
                    color: "text.secondary",
                    ml: 0.5,
                    cursor: "help",
                  }}
                />
              </Tooltip>
            </Box>
          </Box>

          {/* Experience Section */}
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                ...globalStylesToSx("form-section-title"),
                mb: 1.5,
                fontSize: "1rem",
                textTransform: "lowercase",
              }}
            >
              experience
            </Typography>
            {formData.experience.length > 0 &&
              formData.experience.map((exp, index) => (
                <Paper
                  elevation={0}
                  key={index}
                  sx={{ ...globalStylesToSx("form-paper"), mb: 1.5, p: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333333" }}
                    >
                      {exp.company || exp.title || "new experience"}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveListItem("experience", index)}
                      sx={{ fontWeight: 300, textTransform: "none" }}
                    >
                      remove
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        InputLabelProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                        }}
                        inputProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                          maxLength: 50
                        }}
                        label="title"
                        name="title"
                        value={exp.title || ""}
                        onChange={(e) =>
                          handleListItemChange("experience", index, e)
                        }
                        fullWidth
                        variant="outlined"
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <CompanyAutocomplete
                        value={exp.company || ""}
                        onChange={(company: Company) => {
                          const fakeEvent = {
                            target: {
                              name: "company",
                              value: company.name,
                              type: "text",
                            },
                          } as React.ChangeEvent<HTMLInputElement>;
                          handleListItemChange("experience", index, fakeEvent);

                          if (company.domain) {
                            const urlEvent = {
                              target: {
                                name: "company_url",
                                value: `https://${company.domain}`,
                                type: "text",
                              },
                            } as React.ChangeEvent<HTMLInputElement>;
                            handleListItemChange("experience", index, urlEvent);
                          }
                        }}
                        required
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        InputLabelProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                        }}
                        inputProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                          maxLength: 50
                        }}
                        label="company url"
                        name="company_url"
                        value={exp.company_url || ""}
                        onChange={(e) =>
                          handleListItemChange("experience", index, e)
                        }
                        fullWidth
                        variant="outlined"
                        placeholder="https://company.example.com"
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            <Button
              variant="text"
              onClick={() =>
                handleAddListItem("experience", {
                  company: "",
                  title: "",
                  company_url: "",
                  start_date: "",
                  end_date: "",
                } as ExperienceModel)
              }
              disabled={formData.experience.length >= 3 || isFormDisabled}
              sx={{
                ...globalStylesToSx("form-button"),
                ml: -0.5,
                mt: formData.experience.length > 0 ? 0.5 : 0,
              }}
            >
              add experience {formData.experience.length >= 3 && "(max 3)"}
            </Button>
          </Box>

          {/* Contests Section */}
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                ...globalStylesToSx("form-section-title"),
                mb: 1.5,
                fontSize: "1rem",
                textTransform: "lowercase",
              }}
            >
              contests
            </Typography>
            {formData.contests.length > 0 &&
              formData.contests.map((contest, index) => (
                <Paper
                  elevation={0}
                  key={index}
                  sx={{ ...globalStylesToSx("form-paper"), mb: 1.5, p: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333333" }}
                    >
                      {contest.name || "new contest"}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveListItem("contests", index)}
                      sx={{ fontWeight: 300, textTransform: "none" }}
                    >
                      remove
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        InputLabelProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                        }}
                        inputProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                          maxLength: 50
                        }}
                        label="contest name"
                        name="name"
                        value={contest.name || ""}
                        onChange={(e) =>
                          handleListItemChange("contests", index, e)
                        }
                        fullWidth
                        variant="outlined"
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        InputLabelProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                        }}
                        inputProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                          maxLength: 50
                        }}
                        label={`awarded`}
                        name="description"
                        value={contest.description || ""}
                        onChange={(e) =>
                          handleListItemChange("contests", index, e)
                        }
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={1}
                        size="small"
                        placeholder="First Place, etc."
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            <Button
              variant="text"
              onClick={() =>
                handleAddListItem("contests", {
                  name: "",
                  description: "",
                } as ContestModel)
              }
              disabled={formData.contests.length >= 3 || isFormDisabled}
              sx={{
                ...globalStylesToSx("form-button"),
                ml: -0.5,
                mt: formData.contests.length > 0 ? 0.5 : 0,
              }}
            >
              add contest {formData.contests.length >= 3 && "(max 3)"}
            </Button>
          </Box>

          {/* Scores Section */}
          <Box sx={{ mb: 2.5 }} ref={scoresRef}>
            <Typography
              variant="subtitle1"
              sx={{
                ...globalStylesToSx("form-section-title"),
                mb: 1.5,
                fontSize: "1rem",
                textTransform: "lowercase",
              }}
            >
              scores
            </Typography>
            {formData.scores &&
              formData.scores.length > 0 &&
              formData.scores.map((score, index) => (
                <Paper
                  elevation={0}
                  key={index}
                  sx={{ ...globalStylesToSx("form-paper"), mb: 1.5, p: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333333" }}
                    >
                      {score.name || "new score"}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveListItem("scores", index)}
                      sx={{ fontWeight: 300, textTransform: "none" }}
                    >
                      remove
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        InputLabelProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                        }}
                        inputProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                          maxLength: 50
                        }}
                        label="score name"
                        name="name"
                        value={score.name || ""}
                        onChange={(e) =>
                          handleListItemChange("scores", index, e)
                        }
                        fullWidth
                        variant="outlined"
                        size="small"
                        required
                        placeholder="Zetamac, Codeforces, Citation Count."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        InputLabelProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                        }}
                        inputProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                          maxLength: 50
                        }}
                        label="value"
                        name="value"
                        type="number"
                        value={score.value || 0}
                        onChange={(e) =>
                          handleListItemChange("scores", index, e)
                        }
                        fullWidth
                        variant="outlined"
                        size="small"
                        required
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            <Button
              variant="text"
              onClick={() =>
                handleAddListItem("scores", { name: "", value: 0 } as Omit<
                  ScoreModel,
                  "id" | "created_at" | "verified"
                >)
              }
              disabled={formData.scores.length >= 3 || isFormDisabled}
              sx={{
                ...globalStylesToSx("form-button"),
                ml: -0.5,
                mt: formData.scores.length > 0 ? 0.5 : 0,
              }}
            >
              add score {formData.scores.length >= 3 && "(max 3)"}
            </Button>
          </Box>

          {/* Projects Section */}
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                ...globalStylesToSx("form-section-title"),
                mb: 1.5,
                fontSize: "1rem",
                textTransform: "lowercase",
              }}
            >
              works
            </Typography>
            {formData.projects.length > 0 &&
              formData.projects.map((proj, index) => (
                <Paper
                  elevation={0}
                  key={index}
                  sx={{ ...globalStylesToSx("form-paper"), mb: 1.5, p: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333333" }}
                    >
                      {proj.name || "new work"}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveListItem("projects", index)}
                      sx={{ fontWeight: 300, textTransform: "none" }}
                    >
                      remove
                    </Button>
                  </Box>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        InputLabelProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                        }}
                        inputProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                          maxLength: 50
                        }}
                        label="work name"
                        name="name"
                        value={proj.name || ""}
                        onChange={(e) =>
                          handleListItemChange("projects", index, e)
                        }
                        fullWidth
                        variant="outlined"
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        InputLabelProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                        }}
                        inputProps={{
                          sx: { fontWeight: 300, color: "#333333" },
                          maxLength: 50
                        }}
                        label={`description`}
                        name="description"
                        value={proj.description || ""}
                        onChange={(e) =>
                          handleListItemChange("projects", index, e)
                        }
                        fullWidth
                        placeholder="social app with 2000 daus."
                        variant="outlined"
                        multiline
                        rows={1}
                        size="small"
                        required
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            <Button
              variant="text"
              onClick={() =>
                handleAddListItem("projects", {
                  name: "",
                  description: "",
                  url: "",
                } as ProjectModel)
              }
              disabled={formData.projects.length >= 3 || isFormDisabled}
              sx={{
                ...globalStylesToSx("form-button"),
                ml: -0.5,
                mt: formData.projects.length > 0 ? 0.5 : 0,
              }}
            >
              add project {formData.projects.length >= 3 && "(max 3)"}
            </Button>
          </Box>

          {/* Miscellaneous Section */}
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                ...globalStylesToSx("form-section-title"),
                mb: 1.5,
                fontSize: "1rem",
                textTransform: "lowercase",
              }}
            >
              miscellaneous
            </Typography>
            {formData.misc.length > 0 &&
              formData.misc.map((item, index) => (
                <Paper
                  elevation={0}
                  key={index}
                  sx={{ ...globalStylesToSx("form-paper"), mb: 1.5, p: 1.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "#333333" }}
                    >
                      misc. item {index + 1}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveListItem("misc", index)}
                      sx={{ fontWeight: 300, textTransform: "none" }}
                    >
                      remove
                    </Button>
                  </Box>
                  <TextField
                    InputLabelProps={{
                      sx: { fontWeight: 300, color: "#333333" },
                    }}
                    inputProps={{
                      sx: { fontWeight: 300, color: "#333333" },
                      maxLength: 50
                    }}
                    label={`value`}
                    name="value"
                    value={item.value || ""}
                    onChange={(e) => handleListItemChange("misc", index, e)}
                    fullWidth
                    variant="outlined"
                    rows={2}
                    size="small"
                    required
                    placeholder="Rhodes Scholar, Thiel Fellow, etc."
                  />
                </Paper>
              ))}
            <Button
              variant="text"
              onClick={() =>
                handleAddListItem("misc", { value: "" } as MiscInfoModel)
              }
              disabled={formData.misc.length >= 3 || isFormDisabled}
              sx={{
                ...globalStylesToSx("form-button"),
                ml: -0.5,
                mt: formData.misc.length > 0 ? 0.5 : 0,
              }}
            >
              add item {formData.misc.length >= 3 && "(max 3)"}
            </Button>
          </Box>

          {/* Replace the separate preview and save button containers with a single sticky container on mobile */}
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              mt: 2,
              bgcolor: { xs: "#f8f8f8", md: "transparent" },
              pb: { xs: 2, md: 0 },
              pt: { xs: 1, md: 0 },
            }}
          >
            <Box
              sx={{
                display: { xs: "flex", md: "block" },
                flexDirection: { xs: "row", md: "column" },
                gap: { xs: 1, md: 0 },
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: { xs: "block", md: "none" },
                  width: { xs: "50%", md: "100%" },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleOpenPreviewModal}
                  fullWidth
                  sx={{
                    ...globalStylesToSx("form-button"),
                    fontSize: "0.9rem",
                    borderRadius: { xs: 0, md: "0.375rem" },
                  }}
                >
                  preview card
                </Button>
              </Box>
              <Box sx={{ width: { xs: "50%", md: "100%" } }}>
                <Tooltip
                  title={!isFormValid ? validationTooltip : ""}
                  arrow
                  sx={{ display: { xs: "none", md: "block" } }}
                >
                  <span style={{ width: "100%", display: "block" }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveProfile}
                      disabled={
                        uploadStatus === "uploading" ||
                        uploadStatus === "saving" ||
                        !isFormValid ||
                        isFormDisabled
                      }
                      fullWidth
                      size="large"
                      sx={{
                        ...globalStylesToSx("form-button"),
                        ...globalStylesToSx("form-button-primary"),
                        fontSize: "0.9rem",
                        borderRadius: { xs: 0, md: "0.375rem" },
                      }}
                    >
                      {uploadStatus === "saving" ? (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <CircularProgress
                            size={16}
                            color="inherit"
                            sx={{ mr: 1 }}
                          />
                          saving...
                        </Box>
                      ) : (
                        "save profile"
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>

      {/* Right Column: StudentCard Preview - only visible on md+ screens */}
      <Grid
        size={{ xs: 12, md: 5 }}
        sx={{
          height: "100%",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          square
          sx={{
            p: 1,
            flexGrow: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backgroundColor: "#f8f8f8", // Background color from spec
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              textAlign: "center",
              mt: 1,
              mb: 2,
              fontWeight: 300,
              color: "#333333",
              textTransform: "lowercase",
            }}
          >
            live preview
          </Typography>
          {previewData ? (
            <Box sx={{ p: 1 }}>
              <StudentCard
                student={previewData}
                onVote={() => {}}
                isPreview={true}
              />
            </Box>
          ) : (
            <Typography
              color="text.secondary"
              sx={{ textAlign: "center", mt: 2, textTransform: "lowercase" }}
            >
              your profile card will appear here as you fill the form.
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Modal for preview on mobile */}
      <Modal
        open={previewModalOpen}
        onClose={handleClosePreviewModal}
        aria-labelledby="profile-preview-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          sx={{
            backgroundColor: "#f8f8f8",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            width: "100%",
            maxWidth: 500,
            maxHeight: "90vh",
            overflow: "auto",
            position: "relative",
          }}
        >
          <IconButton
            onClick={handleClosePreviewModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#333",
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            id="profile-preview-modal-title"
            variant="h6"
            component="h2"
            sx={{
              textAlign: "center",
              mb: 3,
              mt: 1,
              fontWeight: 300,
              color: "#333333",
              textTransform: "lowercase",
            }}
          >
            profile preview
          </Typography>
          {previewData ? (
            <Box sx={{ width: "100%" }}>
              <StudentCard
                student={previewData}
                onVote={() => {}}
                isPreview={true}
              />
            </Box>
          ) : (
            <Typography
              color="text.secondary"
              sx={{ textAlign: "center", mt: 2, textTransform: "lowercase" }}
            >
              your profile card will appear here as you fill the form.
            </Typography>
          )}
        </Box>
      </Modal>
    </Grid>
  );
};

export default EditProfileForm;
